/**
 * COMMERCE-176A — canonical delivery profile helpers (My Addresses + Checkout).
 * Delivery method is primary; Home/Work labels are not the UX model.
 */
import type { StoreV2Lang } from "./store-v2-locale.js";
import type {
  CustomerAddressDeliveryType,
  CustomerSavedAddressV2,
} from "./customer-profile-v2.js";
import type { NovaPoshtaReferenceSelection } from "./nova-poshta-reference-v1.js";
import { writeCheckoutPrefill } from "./customer-profile-v2.js";
import { normalizeShippingMethod } from "./shipping-method-normalize-v2.js";

export type DeliveryMethodId = "branch" | "parcel_locker" | "address";

export const DELIVERY_METHOD_CARDS: ReadonlyArray<{
  id: DeliveryMethodId;
  deliveryType: CustomerAddressDeliveryType;
  icon: string;
  titleUk: string;
  titleEn: string;
  subtitleUk: string;
  subtitleEn: string;
}> = [
  {
    id: "branch",
    deliveryType: "NOVA_POSHTA_BRANCH",
    icon: "🏢",
    titleUk: "Відділення",
    titleEn: "Branch",
    subtitleUk: "Отримати у відділенні Нової пошти",
    subtitleEn: "Pick up at a Nova Poshta branch",
  },
  {
    id: "parcel_locker",
    deliveryType: "NOVA_POSHTA_PARCEL_LOCKER",
    icon: "📦",
    titleUk: "Поштомат",
    titleEn: "Parcel locker",
    subtitleUk: "Отримати у поштоматі",
    subtitleEn: "Pick up at a parcel locker",
  },
  {
    id: "address",
    deliveryType: "NOVA_POSHTA_COURIER",
    icon: "🚚",
    titleUk: "Кур'єр",
    titleEn: "Courier",
    subtitleUk: "Доставка за адресою",
    subtitleEn: "Courier delivery to your address",
  },
] as const;

export function deliveryTypeFromMode(mode: string | undefined): CustomerAddressDeliveryType {
  const n = normalizeShippingMethod(mode);
  return n.deliveryType ?? "NOVA_POSHTA_BRANCH";
}

export function modeFromDeliveryType(t: CustomerAddressDeliveryType | undefined): DeliveryMethodId {
  const n = normalizeShippingMethod(t);
  return n.uiMode ?? "branch";
}

export function deliveryMethodTitle(
  lang: StoreV2Lang,
  type: CustomerAddressDeliveryType | undefined,
): string {
  const en = lang === "en";
  const card = DELIVERY_METHOD_CARDS.find((c) => c.deliveryType === type) ?? DELIVERY_METHOD_CARDS[0]!;
  return `${card.icon} ${en ? card.titleEn : card.titleUk}`;
}

export function deliveryMethodShortLabel(
  lang: StoreV2Lang,
  type: CustomerAddressDeliveryType | undefined,
): string {
  const en = lang === "en";
  const card = DELIVERY_METHOD_CARDS.find((c) => c.deliveryType === type) ?? DELIVERY_METHOD_CARDS[0]!;
  return en ? card.titleEn : card.titleUk;
}

/** Auto profile title from delivery method — not Home/Work. */
export function defaultProfileLabel(
  lang: StoreV2Lang,
  type: CustomerAddressDeliveryType,
): string {
  return deliveryMethodShortLabel(lang, type);
}

export function formatDeliveryDestinationLine(addr: CustomerSavedAddressV2): string {
  if (addr.delivery_type === "NOVA_POSHTA_COURIER") {
    return [addr.street, addr.building, addr.apartment].filter(Boolean).join(", ") || addr.warehouse;
  }
  if (addr.delivery_type === "NOVA_POSHTA_PARCEL_LOCKER") {
    const n = addr.locker_number || addr.warehouse;
    return n.startsWith("Поштомат") || n.toLowerCase().includes("locker")
      ? n
      : `Поштомат · ${n}`;
  }
  const n = addr.branch_number || addr.warehouse;
  return n;
}

export function selectionFromSavedAddress(
  addr: CustomerSavedAddressV2,
): NovaPoshtaReferenceSelection | null {
  if (addr.np_selection) {
    const mode = normalizeShippingMethod(addr.np_selection.mode).selectionMode;
    if (!mode) return null;
    return { ...addr.np_selection, mode };
  }
  const norm = normalizeShippingMethod(addr.delivery_type);
  if (norm.requiresReselect || !norm.selectionMode) {
    // Preserve contact/city fields at address level; method must be re-chosen in UI.
    return null;
  }
  if (!addr.city && !addr.warehouse) return null;
  const out: NovaPoshtaReferenceSelection = {
    mode: norm.selectionMode,
    settlementRef: addr.settlement_ref ?? "",
    settlementLabel: addr.city,
    locale: "uk",
    version: "117A-v1",
  };
  const whRef = addr.branch_ref || addr.locker_ref || "";
  if (whRef) out.warehouseRef = whRef;
  if (addr.warehouse) out.warehouseLabel = addr.warehouse;
  const whNum = addr.branch_number || addr.locker_number || addr.warehouse;
  if (whNum) out.warehouseNumber = whNum;
  if (addr.street) out.addressLine = addr.street;
  if (addr.building) out.building = addr.building;
  if (addr.apartment) out.flat = addr.apartment;
  return out;
}

/** Apply a saved delivery profile into checkout prefill + return fields for form state. */
export function applyDeliveryProfileToCheckout(addr: CustomerSavedAddressV2): {
  name: string;
  surname: string;
  phone: string;
  city: string;
  warehouse: string;
  note: string;
  np_selection: NovaPoshtaReferenceSelection | null;
} {
  const np = selectionFromSavedAddress(addr);
  const payload = {
    name: addr.name,
    surname: addr.surname,
    phone: addr.phone,
    city: addr.city,
    warehouse: addr.warehouse,
    note: addr.comment ?? "",
    ...(np ? { np_selection: np } : {}),
  };
  writeCheckoutPrefill(payload);
  return {
    name: addr.name,
    surname: addr.surname,
    phone: addr.phone,
    city: addr.city,
    warehouse: addr.warehouse,
    note: addr.comment ?? "",
    np_selection: np,
  };
}
